// ── cohorts-write: honest write-error → message + cohort status vocabulary ───
// Wave 3 (Cohorts). Shared by every cohort write flow (create / edit / complete).
// A write failure is surfaced INLINE — never swallowed, never faked — and a gate
// / permission-denied / unavailable write reads as its honest message, NEVER an
// implied success. The single CO_* → posture map is reused so the modals speak
// the exact same vocabulary as the list/detail reads.

import { ApiClientError } from "../../services/api-client";
import {
  honestGateFromError,
  postureForCoachingError,
  referenceFromError
} from "../../services/coaching-error-posture";

export interface WriteErrorView {
  tone: "error" | "warning";
  message: string;
}

/**
 * Map a write error to an honest, non-leaking top-level message. A closed gate
 * (gate-closed) explicitly states nothing was changed, so a refused write can
 * never imply success (no-fabrication release gate). The support reference is
 * surfaced verbatim for triage; the message is never a stack or a secret.
 */
export function describeCohortWriteError(error: unknown): WriteErrorView | null {
  if (error == null) return null;
  if (!(error instanceof ApiClientError)) {
    return { tone: "error", message: "Couldn't save the cohort. Please try again." };
  }
  const posture = postureForCoachingError(error);
  const reference = referenceFromError(error);
  const ref = reference ? ` (reference: ${reference})` : "";

  if (posture === "permission-denied") {
    return { tone: "error", message: "Your role can't make this change. Ask a super admin if you need it." };
  }
  if (posture === "gate-closed") {
    const gate = honestGateFromError(error);
    return {
      tone: "warning",
      message: `${gate?.reason ?? "This action is closed right now."} No change was made.`
    };
  }
  if (posture === "unavailable") {
    return { tone: "error", message: "Coaching is temporarily unavailable. Try again in a moment." };
  }
  // validation / not-found / generic — show the server message + reference.
  return { tone: "error", message: `${error.message}${ref}` };
}

// ── The cohort status values create/edit may WRITE ─────────────────────────
// FR-COHORT-001: Status is an EDITABLE Select of the valid cohort statuses for
// authorized users (the historical "locked dropdown" bug). The server is
// authoritative — these are the values the form may set. Completed is not one of
// them: a cohort is completed only through the dedicated cohorts.complete confirm
// flow (the API refuses status=completed on create and edit, because completion
// requires 10 completed sessions). The edit form shows Completed only as the
// unchanged current status of an already-completed cohort.
//
// Reads may emit a WIDER status set than create accepts (no DB CHECK on the
// status column — blueprint §1 trap 5), so detail/columns humanize ANY status,
// but the create/edit form only ever WRITES one of these.
export const COHORT_WRITE_STATUSES = ["draft", "active", "paused", "cancelled"] as const;
export type CohortWriteStatus = (typeof COHORT_WRITE_STATUSES)[number];
/** A status the edit form can hold: a writable status, or the unchanged Completed of a completed cohort. */
export type CohortFormStatus = CohortWriteStatus | "completed";

/** The statuses the list/filter exposes as the common operational states. */
export const COHORT_FILTER_STATUSES = ["draft", "active", "paused", "completed", "cancelled"] as const;
